import { isFragment, isFragmented, denormalizedOrderFragmentationKeys } from '@caresend/types';
import { nullishFilter } from '@caresend/utils';
import { indexBookings, indexUsers, indexICD10Codes, indexDraftWaypoints, indexShifts, indexShiftsDescending, indexSupplyShipments, indexSupplyTransfers, indexOffices, indexDenormalizedOrders, indexPlaces, indexProducts } from './indices.js';
import { toastError } from '../../functions/toast/toast.js';

/** Returns an Algolia SearchResponse object */
const algoliaSearch = async (index, query = '', params) => {
    try {
        return index.search(query, { hitsPerPage: 1000, ...params });
    }
    catch (error) {
        throw toastError(error);
    }
};
/** Returns a list of hit objects from an Algolia search */
const getHitsFromAlgoliaSearch = async (index, query = '', params) => {
    const response = await algoliaSearch(index, query, params);
    return response.hits;
};
const getHitsFromAlgoliaSearchWithPagination = async (index, query = '', params) => {
    const response = await algoliaSearch(index, query, params);
    const { hits, nbPages, nbHits, page, query: queryString } = response;
    return {
        hits,
        totalPages: nbPages,
        currentPage: page,
        totalHits: nbHits,
        queryString,
    };
};
const defragmentHits = async (index, fragmentationKeys, getHits) => {
    try {
        const response = await getHits();
        const { hits } = response;
        const defragmentationPromises = hits.map(async (hit) => {
            if (isFragment(hit))
                return null;
            if (!isFragmented(hit))
                return hit;
            const { fragmentIDs } = hit;
            const defragmentedHit = { ...hit };
            const fragmentPromises = fragmentIDs.map((id) => index.getObject(id)
                .catch(() => {
                console.info(`Fragment ${id} not found`);
                return null;
            }));
            const fragments = (await Promise.all(fragmentPromises)).filter(nullishFilter);
            fragments.forEach((fragment) => {
                fragmentationKeys.forEach((key) => {
                    if (!defragmentedHit[key] || !fragment[key])
                        return;
                    defragmentedHit[key] = {
                        ...defragmentedHit[key],
                        ...fragment[key],
                    };
                });
            });
            return defragmentedHit;
        });
        const defragmentedHits = await Promise.all(defragmentationPromises);
        return {
            ...response,
            hits: defragmentedHits.filter(nullishFilter),
        };
    }
    catch (error) {
        throw toastError(error);
    }
};
/** Get SearchResponse */
const searchOfficeBookings = (apiKey, params) => algoliaSearch(indexBookings(apiKey), '', params);
/** Get Hits */
const getPatientsOnOffice = async (apiKey) => getHitsFromAlgoliaSearch(indexUsers(apiKey), '');
const searchICD10Codes = async (apiKey, query, params) => getHitsFromAlgoliaSearch(indexICD10Codes(apiKey), query, params);
const searchBookings = (apiKey, query, params) => getHitsFromAlgoliaSearch(indexBookings(apiKey), query, params);
const searchDraftWaypoints = (apiKey, query, params) => getHitsFromAlgoliaSearch(indexDraftWaypoints(apiKey), query, params);
const searchShifts = (apiKey, query, params) => getHitsFromAlgoliaSearch(indexShifts(apiKey), query, params);
const searchShiftsWithPagination = (apiKey, query, params, reverse) => getHitsFromAlgoliaSearchWithPagination(reverse ? indexShifts(apiKey) : indexShiftsDescending(apiKey), query, params);
const searchSupplyShipments = (apiKey, query, params) => getHitsFromAlgoliaSearch(indexSupplyShipments(apiKey), query, params);
const searchSupplyTransfers = (apiKey, query, params) => getHitsFromAlgoliaSearch(indexSupplyTransfers(apiKey), query, params);
const searchOffices = (apiKey, query, params) => getHitsFromAlgoliaSearch(indexOffices(apiKey), query, params);
const searchUsers = (apiKey, query, params) => getHitsFromAlgoliaSearch(indexUsers(apiKey), query, params);
const searchUsersWithPagination = (apiKey, query, params) => getHitsFromAlgoliaSearchWithPagination(indexUsers(apiKey), query, params);
const searchDenormalizedOrders = (apiKey, query, params) => defragmentHits(indexDenormalizedOrders(apiKey), denormalizedOrderFragmentationKeys, async () => {
    const response = await getHitsFromAlgoliaSearchWithPagination(indexDenormalizedOrders(apiKey), query, params);
    return response;
});
const searchDenormalizedOrdersWithPagination = (apiKey, query, params) => defragmentHits(indexDenormalizedOrders(apiKey), denormalizedOrderFragmentationKeys, async () => {
    const response = await getHitsFromAlgoliaSearchWithPagination(indexDenormalizedOrders(apiKey), query, params);
    return response;
});
const searchPlaces = (apiKey, query, params) => getHitsFromAlgoliaSearch(indexPlaces(apiKey), query, params);
const searchProducts = async (apiKey, query, params) => getHitsFromAlgoliaSearch(indexProducts(apiKey), query, params);
/**
 * Check whether a new algolia secured API key needs to be fetched from cloud.
 */
const needsNewKey = (keyData) => {
    const expiry = keyData === null || keyData === void 0 ? void 0 : keyData.expiry;
    if (expiry === undefined)
        return true;
    const minute = 60 * 1000;
    return (Date.now() + minute) > expiry;
};

export { algoliaSearch, getHitsFromAlgoliaSearch, getHitsFromAlgoliaSearchWithPagination, getPatientsOnOffice, needsNewKey, searchBookings, searchDenormalizedOrders, searchDenormalizedOrdersWithPagination, searchDraftWaypoints, searchICD10Codes, searchOfficeBookings, searchOffices, searchPlaces, searchProducts, searchShifts, searchShiftsWithPagination, searchSupplyShipments, searchSupplyTransfers, searchUsers, searchUsersWithPagination };
